import {
  Button,
  CascaderField,
  Col,
  ErrorMessage,
  FieldLabel,
  Form,
  RemovableCard,
  Row,
  TextInputField,
} from '@/components';
import { usePolicyOptions } from '@/features/master-table/policy/_hooks/policy';
import { useProjectFormContext } from '@/features/project/_hooks/form-context';
import { PlusOutlined } from '@ant-design/icons';
import type { FC } from 'react';
import { type FieldErrors, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const PoliciesArrayField: FC = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
  } = useProjectFormContext();
  const [fiscalYear, inputPoliciesForProject] = watch([
    'data.fiscalYear',
    'data.policiesForProject',
  ]);

  const currentFiscalYear = fiscalYear?.year().toString();

  const policyOptions = usePolicyOptions({
    params: { referenceFiscalYear: currentFiscalYear },
    enabled: !!currentFiscalYear,
  });

  const hasPoliciesForProjectRootError = !!(
    errors.data &&
    ('policiesForProject' in errors.data
      ? (errors.data.policiesForProject as FieldErrors)?.root
      : null) &&
    !inputPoliciesForProject?.length
  );

  const {
    fields: policiesForProjectField,
    append: appendPoliciesForProject,
    remove: removePoliciesForProject,
  } = useFieldArray({
    control,
    name: 'data.policiesForProject',
  });
  return (
    <Form.Item
      label={
        <FieldLabel
          text={t('policy')}
          hint={t('hint-policy-for-project')}
          required
        />
      }
    >
      <Row gutter={[24, 8]}>
        {policiesForProjectField.map((item, idx) => {
          return (
            <Col key={item.id} span={24}>
              <RemovableCard
                onRemove={() => {
                  removePoliciesForProject(idx);
                }}
              >
                <Row gutter={[24, 8]} wrap={false}>
                  <Col span={12}>
                    <CascaderField
                      name={`data.policiesForProject.${idx}.policyId`}
                      label={t('policy')}
                      control={control}
                      errors={errors}
                      options={policyOptions.data}
                      required
                      loading={policyOptions.isPending}
                    />
                  </Col>
                  <Col span={12}>
                    <TextInputField
                      name={`data.policiesForProject.${idx}.policyUrl`}
                      autocompleteKey="rsProject.policyUrl"
                      label={t('policy-url')}
                      hint={t('hint-project-policy-url')}
                      control={control}
                      errors={errors}
                    />
                  </Col>
                </Row>
              </RemovableCard>
            </Col>
          );
        })}
        <Col span={12}>
          <Button
            type="dashed"
            onClick={() => {
              appendPoliciesForProject([
                {
                  policyId: [],
                  policyUrl: null,
                },
              ]);
            }}
            block
            icon={<PlusOutlined />}
          >
            {t('add-project-policy')}
          </Button>
        </Col>
      </Row>
      {hasPoliciesForProjectRootError && (
        <ErrorMessage errors={errors} name="data.policiesForProject.root" />
      )}
    </Form.Item>
  );
};
